import {
  Card as AntdCard,
  Badge,
  Button,
  Col,
  Collapse,
  Descriptions,
  Row,
  Tag,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import React from "react";
import {
  PaymentAccount,
  PaymentAccountTypesEnum,
} from "../helpers/shared-interfaces";
import { bankAccountTypes } from "../helpers/utilities";
import CardSection from "./CardSection";
import { tabPaneStyle } from "../helpers/constants";
import EntitledSection from "./EntitledSection";
import entitlements from "../config/entitlements";
import VerifiedMark from "./VerifiedMark";

interface Props {
  style?: React.CSSProperties;
  payoutAccounts: PaymentAccount[];
  debitAccounts?: PaymentAccount[];
  onAddPayoutAccount?: () => void;
  onAddDebitAccount?: () => void;
  onEditPayoutAccount?: (account: PaymentAccount) => void;
  onDeletePayoutAccount?: (account: PaymentAccount) => void;
  onEditDebitAccount?: (account: PaymentAccount) => void;
  onDeleteDebitAccount?: (account: PaymentAccount) => void;
  editEntitlement?: string;
  deleteEntitlement?: string;
  bankDetailsTitle?: string;
}

const { Panel } = Collapse;

export function VerifiedTag(verified: boolean): JSX.Element {
  let color, text;
  if (verified) {
    color = "#87d068";
    text = "Verified";
  } else {
    color = "#f72916";
    text = "Not Verified";
  }

  return (
    <Tag color={color} key={text}>
      {text}
    </Tag>
  );
}

export const PaymentAccountCard = (props: {
  paymentAccount: PaymentAccount;
}): JSX.Element => {
  if (
    props.paymentAccount.accountType === PaymentAccountTypesEnum.bankAccount
  ) {
    return (
      <div>
        <CardSection
          headingText="Name"
          data={
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {props.paymentAccount.name}
              {(props.paymentAccount.isPayoutAccountVerified || props.paymentAccount.isVerified) && <VerifiedMark />}
            </span>
          }
        />

        {props.paymentAccount.bankBranch && (
          <CardSection
            headingText="Institution"
            data={props.paymentAccount.bankBranch.financialInstitution.name}
          />
        )}
        {props.paymentAccount.bankBranch && (
          <CardSection
            headingText="Branch"
            data={props.paymentAccount.bankBranch.name}
          />
        )}
        {props.paymentAccount.bankAccountType && (
          <CardSection
            headingText="Account Type"
            data={bankAccountTypes[props.paymentAccount.bankAccountType]}
          />
        )}
        {props.paymentAccount.accountNumber && (
          <CardSection
            headingText="Account Number"
            data={props.paymentAccount.accountNumber.toString()}
          />
        )}
        {props.paymentAccount.bankBranch && (
          <CardSection
            headingText="Sort Code"
            data={props.paymentAccount.bankBranch?.sortCode ?? ""}
          />
        )}
        {props.paymentAccount.customerSubscription && (
          <CardSection
            headingText="Product"
            data={props.paymentAccount.customerSubscription?.product?.name}
          />
        )}
        {props.paymentAccount.amount && (
          <CardSection
            headingText="Amount"
            data={props.paymentAccount.amount?.toString() as string}
          />
        )}

        {props.paymentAccount.expirationDate && (
          <CardSection
            headingText="Valid Until"
            data={props.paymentAccount.expirationDate}
          />
        )}

        {props.paymentAccount.authorizationFormUrl && (
          <CardSection
            headingText="Debit Authorization Form"
            data={
              <a
                target="_blank"
                href={props.paymentAccount.authorizationFormUrl}
                rel="noopener noreferrer"
              >
                Click To Open
              </a>
            }
          />
        )}
      </div>
    );
  } else if (
    props.paymentAccount.accountType === PaymentAccountTypesEnum.cheque
  ) {
    return (
      <div>
        <CardSection
          headingText="Name"
          data={
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {props.paymentAccount.name}
              {props.paymentAccount.isPayoutAccountVerified && <VerifiedMark />}
            </span>
          }
        />
        {props.paymentAccount.bankBranch && (
          <CardSection
            headingText="Institution"
            data={props.paymentAccount.bankBranch.financialInstitution.name}
          />
        )}
        {props.paymentAccount.amount && (
          <CardSection
            headingText="Amount"
            data={props.paymentAccount.amount?.toString() as string}
          />
        )}
      </div>
    );
  }
  return (
    <div>
      {props.paymentAccount.institutionName && (
        <CardSection
          headingText="Institution"
          data={props.paymentAccount.institutionName}
        />
      )}
      <CardSection
        headingText="Name"
        data={
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {props.paymentAccount.name}
            {props.paymentAccount.isPayoutAccountVerified && <VerifiedMark />}
          </span>
        }
      />
      {props.paymentAccount.bankBranch && (
        <>
          <CardSection
            headingText="Institution"
            data={props.paymentAccount.bankBranch.name}
          />

          <CardSection
            headingText="Sort Code"
            data={props.paymentAccount.bankBranch?.sortCode ?? ""}
          />
        </>
      )}
      <CardSection
        headingText="Phone Number"
        data={props.paymentAccount.accountNumber}
      />

      {props.paymentAccount.amount && (
        <CardSection
          headingText="Amount"
          data={props.paymentAccount.amount?.toString() as string}
        />
      )}

      {props.paymentAccount.customerSubscription && (
        <CardSection
          headingText="Product"
          data={props.paymentAccount.customerSubscription?.product.name}
        />
      )}

    </div>
  );
};

export default class PaymentAccountsView extends React.Component<
  Props,
  Record<string, unknown>
> {
  constructor(props: Props) {
    super(props);
    this.state = {};
  }

  render(): JSX.Element {
    const payoutAccounts = this.props.payoutAccounts;
    const bank = payoutAccounts.filter(account => account.accountType !== 'mm_account');
    const debitAccounts = this.props.debitAccounts ?? [];
    const showDebitAccounts =
      debitAccounts.length > 0 ||
      Boolean(
        this.props.onAddDebitAccount ||
          this.props.onEditDebitAccount ||
          this.props.onDeleteDebitAccount,
      );
    const editEntitlement =
      this.props.editEntitlement || entitlements.Customers.edit;
    const deleteEntitlement =
      this.props.deleteEntitlement ||
      entitlements.Customers.payoutAccount.delete;

    return (
      <div
        style={{
          border: "1px solid rgb(232 232 232)",
          padding: "20px",
          boxSizing: "border-box",
          ...this.props.style,
        }}
      >
        <Descriptions
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span>Payment Accounts</span>
            </div>
          }
        ></Descriptions>
        <Collapse>
          <Panel
            header={
              <span>
                {this.props.bankDetailsTitle || "Customer Bank Details"}{" "}
                <Badge
                  count={bank.length}
                  style={tabPaneStyle}
                />
              </span>
            }
            key="bank"
          >
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <EntitledSection entitlement={editEntitlement}>
                {this.props.onAddPayoutAccount && (
                  <Button
                    className="base-button"
                    onClick={this.props.onAddPayoutAccount}
                    icon={<PlusOutlined />}
                  >
                    Add Payout Account
                  </Button>
                )}
              </EntitledSection>
            </div>
            <Row gutter={[16, 16]}>
              {bank.map(
                (payoutAccount: PaymentAccount): JSX.Element => {
                  return (
                    <Col key={payoutAccount.id} span={6}>
                      <AntdCard
                        className="associated-entity-card"
                        key={payoutAccount.id}
                      >
                        {PaymentAccountCard({
                          paymentAccount: payoutAccount,
                        })}
                        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                          <EntitledSection entitlement={editEntitlement}>
                            {this.props.onEditPayoutAccount &&
                              payoutAccount.customerSubscription?.product
                                ?.isActive !== false && (
                                <Button
                                  icon={<EditOutlined />}
                                  onClick={() =>
                                    this.props.onEditPayoutAccount?.(
                                      payoutAccount,
                                    )
                                  }
                                  style={actionButtonStyle}
                                ></Button>
                              )}
                          </EntitledSection>
                          <EntitledSection entitlement={deleteEntitlement}>
                            {this.props.onDeletePayoutAccount && (
                              <Button
                                icon={<DeleteOutlined />}
                                onClick={() =>
                                  this.props.onDeletePayoutAccount?.(payoutAccount)
                                }
                                danger
                              >
                              </Button>
                            )}
                          </EntitledSection>
                        </div>
                      </AntdCard>
                    </Col>
                  );
                },
              )}
            </Row>
          </Panel>

          {showDebitAccounts && (
            <Panel
              header={
                <span>
                  Debit Accounts{" "}
                  <Badge count={debitAccounts.length} style={tabPaneStyle} />
                </span>
              }
              key="debitAccounts"
            >
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <EntitledSection entitlement={editEntitlement}>
                  {this.props.onAddDebitAccount && (
                    <Button
                      className="base-button"
                      onClick={this.props.onAddDebitAccount}
                      icon={<PlusOutlined />}
                    >
                      Add Debit Account
                    </Button>
                  )}
                </EntitledSection>
              </div>
              <Row gutter={[16, 16]}>
                {debitAccounts.map(
                  (debitAccount: PaymentAccount): JSX.Element => {
                    return (
                      <Col key={debitAccount.id} span={6}>
                        <AntdCard
                          className="associated-entity-card"
                          key={debitAccount.id}
                        >
                          {PaymentAccountCard({
                            paymentAccount: debitAccount,
                          })}
                          <div
                            style={{ display: "flex", gap: 8, marginTop: 10 }}
                          >
                            <EntitledSection entitlement={editEntitlement}>
                              {this.props.onEditDebitAccount &&
                                debitAccount.customerSubscription?.product
                                  ?.isActive !== false && (
                                  <Button
                                    icon={<EditOutlined />}
                                    onClick={() =>
                                      this.props.onEditDebitAccount?.(
                                        debitAccount,
                                      )
                                    }
                                    style={actionButtonStyle}
                                  ></Button>
                                )}
                            </EntitledSection>
                            <EntitledSection entitlement={deleteEntitlement}>
                              {this.props.onDeleteDebitAccount && (
                                <Button
                                  danger
                                  data-cy="test-user-delete-button"
                                  icon={<DeleteOutlined />}
                                  onClick={() =>
                                    this.props.onDeleteDebitAccount?.(
                                      debitAccount,
                                    )
                                  }
                                ></Button>
                              )}
                            </EntitledSection>
                          </div>
                        </AntdCard>
                      </Col>
                    );
                  },
                )}
              </Row>
            </Panel>
          )}
        </Collapse>
      </div>
    );
  }
}
const actionButtonStyle: React.CSSProperties = {
  borderStyle: "dashed",
};
